import { useContext, useState, useEffect, useMemo } from "react";
import { LangContext, UiTextsContext, UserDataContext } from "../../contexts/Contexts";
import { Button } from "antd";
import { FollowUser } from "./FollowUser";
import { FollowedUser } from "./FollowedUser";
import { ConnectedUser } from "./ConnectedUser";
import UnFollow from "../publications/actions/UnFollow";
import {
  followUserService,
  unFollowUserService,
} from "../../../infrastructure/services/UserInfoService";
import { USER_CONNECTIONS_CHANGED_EVENT } from "../../../infrastructure/hooks/useGetFollowers";

const normalizeRelation = (relation) =>
  String(relation || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const FollowActions = ({ user, profPic, relation, canSendMessage = true }) => {
  const { userData } = useContext(UserDataContext);
  const { texts } = useContext(UiTextsContext);
  const { lang } = useContext(LangContext);
  const [sendReq, setSendReq] = useState(false);
  const [loadingPendingAction, setLoadingPendingAction] = useState(false);
  const [effectiveRelation, setEffectiveRelation] = useState(relation || "");
  const isSpanish = String(lang || "en").toLowerCase().startsWith("es");
  const labels = {
    sendMessage: isSpanish ? "Enviar mensaje" : "Send message",
    follow: isSpanish ? "Seguir" : "Follow",
    followBack: isSpanish ? "Seguir de vuelta" : "Follow back",
    following: isSpanish ? "Siguiendo" : "Following",
    unfollow: isSpanish ? "Dejar de seguir" : "Unfollow",
    cancel: isSpanish ? "Cancelar" : "Cancel",
  };

  useEffect(() => {
    setEffectiveRelation(relation || "");
    setSendReq(false);
    setLoadingPendingAction(false);
  }, [relation]);

  const normalizedRelation = useMemo(
    () => normalizeRelation(effectiveRelation),
    [effectiveRelation],
  );
  const isOwnProfile = String(userData?.idUsuario ?? "") === String(user?.idUsuario ?? "");
  const isConnected = normalizedRelation.includes("conexi");
  const isNoRelation = !normalizedRelation || normalizedRelation.includes("ninguno");
  const isPending = normalizedRelation.includes("pendiente");
  const shouldFollowBack =
    normalizedRelation.includes("seguir") &&
    !normalizedRelation.includes("siguiendo");
  const isFollowing =
    normalizedRelation.includes("seguido") ||
    normalizedRelation.includes("siguiendo") ||
    normalizedRelation.includes("activa");

  const onClickUnFollow = async () => {
    try {
      const req = {
        IdUsuario: userData?.idUsuario,
        IdAmigo: user?.idUsuario,
      };
      await unFollowUserService(req);
      window.dispatchEvent(
        new CustomEvent(USER_CONNECTIONS_CHANGED_EVENT, { detail: req }),
      );
      setEffectiveRelation("Ninguno");
      setSendReq(false);
    } catch (error) {
      console.error("Failed unfollow user ", error);
    }
  };

  const onClickCancelFollowRequest = async () => {
    try {
      const req = {
        idUsuario: userData?.idUsuario,
        idAmigo: user?.idUsuario,
      };

      setLoadingPendingAction(true);
      const response = await followUserService(req);
      window.dispatchEvent(
        new CustomEvent(USER_CONNECTIONS_CHANGED_EVENT, { detail: req }),
      );

      if (String(response?.accion || "").toUpperCase() === "CANCELADA") {
        setEffectiveRelation("Ninguno");
        setSendReq(false);
        return;
      }

      if (String(response?.accion || "").toUpperCase() === "ENVIADA") {
        setEffectiveRelation("Pendiente");
      }
    } catch (error) {
      console.error("Failed cancel follow request ", error);
    } finally {
      setLoadingPendingAction(false);
    }
  };

  // const optionsButton = (
  //   <Button
  //     style={{
  //       background: "#f18b6f",
  //       borderColor: "#f18b6f",
  //       color: "white",
  //       padding: "10px",
  //       fontSize: "16px",
  //       fontWeight: "bold",
  //       borderRadius: "999px",
  //       minWidth: "10px",
  //     }}
  //   >
  //     ...
  //   </Button>
  // );

  if (isConnected) {
    return (
      <>
        <ConnectedUser
          userData={userData}
          connectedUser={user}
          profPic={profPic}
          texts={texts}
          canSendMessage={canSendMessage}
          label={labels.sendMessage}
        />
        <UnFollow text={labels.unfollow} onClick={onClickUnFollow} />
        {/* <div style={{ marginLeft: "1vw" }}>{optionsButton}</div> */}
      </>
    );
  }

  if (
    isNoRelation &&
    isOwnProfile
  ) {
    return <></>;
  }

  if (isNoRelation) {
    return (
      <>
        <ConnectedUser
          userData={userData}
          connectedUser={user}
          profPic={profPic}
          texts={texts}
          canSendMessage={canSendMessage}
          label={labels.sendMessage}
        />
        <FollowUser
          userData={userData}
          userToFollow={user}
          texts={texts}
          disable={sendReq}
          setSendReq={setSendReq}
          back={false}
          label={labels.follow}
          onSuccess={() => setEffectiveRelation("Pendiente")}
        />
        {/* <div style={{ marginLeft: "1vw" }}>{optionsButton}</div> */}
      </>
    );
  }

  if (isPending) {
    return (
      <>
        <ConnectedUser
          userData={userData}
          connectedUser={user}
          profPic={profPic}
          texts={texts}
          canSendMessage={canSendMessage}
          label={labels.sendMessage}
        />
        <div style={{ marginLeft: "1vw" }}>
          <Button
            onClick={onClickCancelFollowRequest}
            disabled={loadingPendingAction}
            loading={loadingPendingAction}
            style={{
              background: "#f18b6f",
              borderColor: "#f18b6f",
              color: "#fff",
              padding: "12px 10px",
              fontSize: "16px",
              fontWeight: "bold",
              borderRadius: "999px",
              minWidth: "10px",
            }}
          >
            {labels.cancel}
          </Button>
        </div>
      </>
    );
  }

  if (shouldFollowBack) {
    return (
      <>
        <ConnectedUser
          userData={userData}
          connectedUser={user}
          profPic={profPic}
          texts={texts}
          canSendMessage={canSendMessage}
          label={labels.sendMessage}
        />
        <FollowUser
          userData={userData}
          userToFollow={user}
          texts={texts}
          disable={sendReq}
          setSendReq={setSendReq}
          back={true}
          label={labels.followBack}
          onSuccess={() => setEffectiveRelation("Pendiente")}
        />
        {/* <div style={{ marginLeft: "1vw" }}>{optionsButton}</div> */}
      </>
    );
  }

  if (isFollowing) {
    return (
      <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center"}}>
        <ConnectedUser
          userData={userData}
          connectedUser={user}
          profPic={profPic}
          texts={texts}
          canSendMessage={canSendMessage}
          label={labels.sendMessage}
        />
        <FollowedUser
          userData={userData}
          followedUser={user}
          texts={texts}
          label={labels.following}
        />
        <div style={{ marginLeft: "1vw" }}>
          <UnFollow text={labels.unfollow} onClick={onClickUnFollow} />
        </div>
        {/* <Col style={{marginLeft: "1vw"}}>
          {optionsButton}
        </Col> */}
      </div>
    );
  }

  //bloqueado no aparece
  //si me sigue, cambiar texto a seguir de vuelta

  return null;
};
